import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import { SearchMenuStates } from 'components/Search/type'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import Group from 'components/utils/Group'
import { pluralizeToString } from 'lib/string/pluralize'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import AssistChip from 'LuxKit/Chips/AssistChip'
import BusinessTravellerSelectHotelTravellers from './BusinessTravellerSelectHotelsTravellers'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import BusinessTravellerSelectHotelsRoomsDesktop from './BusinessTravellerSelectHotelsRoomsDesktop'

interface MappedStateProps {
  travellerEmployees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>
  guestCount: number
}

interface Props {
  className?: string;
  activeMenu: SearchMenuStates;
  onMenuDismiss: () => void
  openEmployeeMenu: (e: React.MouseEvent<HTMLButtonElement>) => void
  openRoomMenu: (e: React.MouseEvent<HTMLButtonElement>) => void
}

function BusinessTravellerSelectHotelsDesktop(props: Props & MappedStateProps) {
  const {
    className,
    activeMenu,
    travellerEmployees,
    guestCount,
    openEmployeeMenu,
    openRoomMenu,
    onMenuDismiss,
  } = props

  const state = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const occupancies = useMemo(() => state.occupancies ?? [], [state.occupancies])

  const localInputRef = useRef<HTMLButtonElement>(null)
  const roomInputRef = useRef<HTMLButtonElement>(null)
  const [error, setError] = useState<string>()

  const totalTravellers = travellerEmployees.length + guestCount
  const roomCount = totalTravellers === 0 ? 0 : occupancies.length

  const fieldValue = useMemo(() => {
    if (totalTravellers === 0) {
      return undefined
    }
    const primaryTraveller = travellerEmployees[0]
    if (!primaryTraveller) {
      return `${totalTravellers} guests`
    }
    if (totalTravellers === 1) {
      return `${primaryTraveller.firstName} ${primaryTraveller.lastName}`
    }
    return `${primaryTraveller.firstName} ${primaryTraveller.lastName} +${totalTravellers - 1} more`
  }, [travellerEmployees, totalTravellers])

  const setActiveMenu = useCallback((menu: SearchMenuStates) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu })
  }, [globalSearchDispatch])

  const handleSelectTravellerClose = useCallback(() => {
    onMenuDismiss()
  }, [onMenuDismiss])

  const handleSelectTravellerApply = useCallback(() => {
    // Go to Rooms menu if there are multiple travellers
    if (totalTravellers > 1) {
      setActiveMenu(SearchMenuStates.BusinessTravellerMultiRoomsHotelSelect)
    } else {
      // Otherwise just close the menu
      onMenuDismiss()
    }
  }, [totalTravellers, setActiveMenu, onMenuDismiss])

  const handleSelectRoomClose = useCallback(() => {
    onMenuDismiss()
  }, [onMenuDismiss])

  const handleSelectRoomApply = useCallback(() => {
    onMenuDismiss()
  }, [onMenuDismiss])

  return (
    <SearchFormFieldGroup className={className}>
      <HiddenInput
        value={fieldValue}
        name="travellers"
        required
        invalidMessage="Please select traveller"
        onErrorUpdate={setError}
      />
      <Group direction="horizontal" gap={12}>
        <AssistChip
          variant="outline"
          size="medium"
          startIcon={<LineUserIcon />}
          endIcon={<LineAngleDownIcon />}
          onClick={openEmployeeMenu}
          ref={localInputRef}
          error={!!error}
        >
          {fieldValue && <>Travellers: <b>{fieldValue}</b></>}
          {!fieldValue && 'Add travellers'}
        </AssistChip>
        <AssistChip
          variant="outline"
          size="medium"
          endIcon={<LineAngleDownIcon />}
          onClick={openRoomMenu}
          ref={roomInputRef}
        >
          {pluralizeToString('room', roomCount)}
        </AssistChip>
      </Group>
      <BusinessTravellerSelectHotelTravellers
        activeMenu={activeMenu}
        onClose={handleSelectTravellerClose}
        onApply={handleSelectTravellerApply}
        inputRef={localInputRef}
        totalTravellers={totalTravellers}
      />
      <BusinessTravellerSelectHotelsRoomsDesktop
        activeMenu={activeMenu}
        onClose={handleSelectRoomClose}
        onApply={handleSelectRoomApply}
        roomInputRef={roomInputRef}
      />
    </SearchFormFieldGroup>
  )
}

export default connect<MappedStateProps, {}, Props, App.State>((state) => {
  return {
    travellerEmployees: selectSelectedTravellerEmployees(state),
    guestCount: state.businessTraveller.selectedTravellerGuestCount,
  }
})(BusinessTravellerSelectHotelsDesktop)
